import {
  ChangeEvent,
  FunctionComponent,
  useCallback,
  useEffect,
  useState
} from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router";

import {
  Checkbox,
  DataError,
  SearchInput,
  Table,
  TableDataCell,
  TableHeadCell,
  Typography
} from "@digital-services/ui";
import { Pagination, useSortQueryString } from "@ds-cps/ui";

import { InviteOrganizationAdminButton } from "@organization/components/adminAssociation/InviteOrganizationAdminButton";
import RemoveOrganizationAdminButton from "@organization/components/adminAssociation/RemoveOrganizationAdminButton";
import { OrganizationUserDetails } from "@organization/models/OrganizationUserDetails";
import {
  filter,
  sort,
  useGetOrganizationAdmins
} from "@organization/services/use-get-organization-admins";
import { UserStatusIcon } from "@user/components/UserStatusIcon";

import { useUserContext } from "@hooks/useUserContext";
import { formatterShort } from "@utils/time-utils";

const OrganizationAdmins: FunctionComponent = () => {
  const { organizationId } = useParams();
  const { user } = useUserContext();

  const { t } = useTranslation(undefined, { keyPrefix: "organization.admin" });
  const { t: tAdmins } = useTranslation(undefined, {
    keyPrefix: "organization.admins"
  });

  const [filteredAdmins, setFilteredAdmins] = useState<
    OrganizationUserDetails[] | null
  >(null);
  const [organizationAdmins, setOrganizationAdmins] = useState<
    OrganizationUserDetails[] | null
  >(null);

  const [searchValue, setSearchValue] = useState<string | undefined>("");

  const nameColumnName = "name";
  const emailColumnName = "email";
  const statusColumnName = "status";
  const invitedOnColumnName = "invited-on";

  // Tech Note. In this component Sorting of table entries done via JavaScript aka Frontend Sorting
  const { activeSort, sortDirection, handleSort } = useSortQueryString({
    defaultSort: nameColumnName,
    defaultDirection: "ascending"
  });

  const [checkedAll, setCheckedAll] = useState<boolean>(false);
  const [checkedUsers, setCheckedUsers] = useState<OrganizationUserDetails[]>(
    []
  );

  const { admins, isLoading, error, mutate } = useGetOrganizationAdmins(
    organizationId ?? ""
  );

  useEffect(() => {
    const users = filter(admins, searchValue);
    setFilteredAdmins(users);
  }, [organizationId, admins, searchValue]);

  useEffect(() => {
    const users = sort(filteredAdmins, activeSort, sortDirection);
    setOrganizationAdmins(users);
  }, [filteredAdmins, activeSort, sortDirection]);

  useEffect(() => {
    if (user.isSuperAdmin) {
      const checkedRows: OrganizationUserDetails[] = [];

      organizationAdmins?.forEach(user => {
        const checkBox: HTMLInputElement = document.getElementById(
          user.id
        ) as HTMLInputElement;
        checkBox.checked = checkedAll;
        if (checkedAll === true) {
          checkedRows.push(user);
        }
      });

      setCheckedUsers(checkedRows);
    }
  }, [user, checkedAll, organizationAdmins]);

  function renderNameHeader() {
    return (
      <TableHeadCell
        sortActive={activeSort === nameColumnName}
        sortDirection={sortDirection}
        onSort={() => handleSort(nameColumnName)}
        data-testid={`${nameColumnName}-column`}
      >
        <span>{t(`${nameColumnName}.column-header`)}</span>
      </TableHeadCell>
    );
  }

  const renderTableHeadCell = (columnName: string) => {
    return (
      <TableHeadCell
        sortActive={activeSort === columnName}
        sortDirection={sortDirection}
        onSort={() => handleSort(columnName)}
      >
        <div data-testid={`${columnName}-column`}>
          {t(`${columnName}.column-header`)}
        </div>
      </TableHeadCell>
    );
  };

  function handleToggleSelectRow(user: OrganizationUserDetails): void {
    setCheckedUsers(
      checkedUsers.includes(user)
        ? checkedUsers.filter(el => el !== user)
        : [...checkedUsers, user]
    );
  }

  const renderSelectionColumn = (user: OrganizationUserDetails) => {
    return (
      <TableDataCell className="w-1">
        <div data-testid={user.id}>
          <Checkbox
            id={user.id}
            data-testid={`checkbox-${user.id}`}
            variant="alternative"
            label={undefined}
            disabled={organizationAdmins?.length === 1}
            onChange={() => handleToggleSelectRow(user)}
          />
        </div>
      </TableDataCell>
    );
  };

  const renderSelectAllTableHeaderCell = () => {
    return (
      <TableHeadCell className="w-1">
        <Checkbox
          id="checkAll"
          data-testid="check-all"
          variant="alternative"
          checked={checkedAll}
          disabled={!organizationAdmins || organizationAdmins.length <= 1}
          label={undefined}
          onChange={handleToggleSelectAll}
        />
      </TableHeadCell>
    );
  };

  const renderStatusColumn = (state: string) => {
    return (
      <TableDataCell className="py-0!">
        <div className="flex gap-2">
          <UserStatusIcon state={state} />
          {state}
        </div>
      </TableDataCell>
    );
  };

  function handleToggleSelectAll(event: ChangeEvent<HTMLInputElement>): void {
    setCheckedAll(event.target.checked);
  }

  function handleOnAfterDelete() {
    setCheckedAll(false);
    mutate();
  }

  // Relying on useCallback() is workaround to address issue with `<SearchInput>` whose `onChange()` is executed twice.
  const handleSearchInput = useCallback((newValue: string) => {
    setSearchValue(newValue);
  }, []);

  return (
    <div
      data-testid="organization-admin-table-container"
      className="mt-3 bg-white p-4"
    >
      <div className="mb-3 flex items-center gap-3">
        <div className="grow">
          {checkedUsers.length > 0 ? (
            <div
              className="float-left flex items-center gap-1"
              data-testid="delete-users"
            >
              <Typography variant="smallTitle">
                {tAdmins("selected.message", { count: checkedUsers.length })}
              </Typography>
              <RemoveOrganizationAdminButton
                organizationId={organizationId ?? ""}
                admins={checkedUsers}
                isAllSelected={checkedUsers.length === admins?.length}
                onRemove={handleOnAfterDelete}
              />
            </div>
          ) : (
            ""
          )}
        </div>
        <div className="w-[500px]">
          <SearchInput
            data-testid="search-admin"
            placeholder={tAdmins("search.placeholder")}
            onChange={handleSearchInput}
          />
        </div>
        {user.isSuperAdmin ? (
          <InviteOrganizationAdminButton
            organizationId={organizationId ?? ""}
            afterCallback={() => {
              mutate();
            }}
          />
        ) : null}
      </div>

      {error ? (
        <DataError
          message={tAdmins("messages.loading-error")}
          variant="primary"
        />
      ) : null}

      {!error && (
        <>
          <Table className="w-full" isLoading={isLoading} rowHoverHighlight>
            <thead data-testid="thead">
              <tr className="leading-[0px]">
                {user.isSuperAdmin ? renderSelectAllTableHeaderCell() : null}
                {renderNameHeader()}
                {renderTableHeadCell(emailColumnName)}
                {renderTableHeadCell(invitedOnColumnName)}
                {renderTableHeadCell(statusColumnName)}
              </tr>
            </thead>
            <tbody data-testid="tbody">
              {organizationAdmins?.map(admin => (
                <tr
                  key={`table-row-${admin.id}`}
                  data-testid={`table-row-${admin.id}`}
                >
                  {user.isSuperAdmin ? renderSelectionColumn(admin) : null}
                  <TableDataCell>{admin.displayName}</TableDataCell>
                  <TableDataCell>{admin.emailAddress}</TableDataCell>
                  <TableDataCell>
                    {formatterShort(admin.createdOn)}
                  </TableDataCell>
                  {renderStatusColumn(admin.state)}
                </tr>
              ))}
            </tbody>
          </Table>

          {organizationAdmins && organizationAdmins.length > 0 ? (
            <Pagination totalCount={organizationAdmins.length} />
          ) : null}
        </>
      )}
    </div>
  );
};

export default OrganizationAdmins;
